import { getExampleNumber } from 'libphonenumber-js';
import examples from 'libphonenumber-js/examples.mobile.json';
import { defaultPhoneCountry } from '@/utils/phone';

/**
 * Example text shown inside empty fields. Everything here follows the visitor's rough location so
 * the examples read as local, and everything degrades quietly: city, then region, then country,
 * then wording with no place in it at all. Nothing here is ever submitted; they are placeholders.
 */

const fallbackVenue = 'City, Country';
const fallbackTitle = 'Gala dinner 🍽️ at the Ritz';
const fallbackDescription = 'Gala dinner for 200 guests at a hotel downtown';
const fallbackPhone = '+41 79 123 45 67 or 079 123 45 67';

const countryNames = {
  AE: 'United Arab Emirates',
  AT: 'Austria',
  AU: 'Australia',
  BE: 'Belgium',
  CA: 'Canada',
  CH: 'Switzerland',
  DE: 'Germany',
  DK: 'Denmark',
  ES: 'Spain',
  FI: 'Finland',
  FR: 'France',
  GB: 'United Kingdom',
  HK: 'Hong Kong',
  IE: 'Ireland',
  IT: 'Italy',
  LT: 'Lithuania',
  NL: 'Netherlands',
  NO: 'Norway',
  NZ: 'New Zealand',
  PL: 'Poland',
  PT: 'Portugal',
  SE: 'Sweden',
  US: 'United States',
};

export function countryName(code) {
  if (!code || !/^[A-Za-z]{2}$/.test(code)) return '';

  const upper = code.toUpperCase();

  try {
    // Not in older Safari, hence the table above
    const display = new Intl.DisplayNames(['en'], { type: 'region' }).of(upper);
    if (display && display !== upper) return display;
  } catch (error) {
    // fall through to the table
  }

  return countryNames[upper] || '';
}

/** "Sydney, Australia" → "New South Wales, Australia" → "Australia" → "City, Country" */
export function venuePlaceholder({ city, region, country } = {}) {
  const place = city || region || '';
  const name = countryName(country);

  if (place && name) return `${place}, ${name}`;
  if (place) return place;
  if (name) return name;

  return fallbackVenue;
}

export function eventTitlePlaceholder({ city, region } = {}) {
  const place = city || region;

  return place ? `Gala dinner 🍽️ in ${place}` : fallbackTitle;
}

export function eventDescriptionPlaceholder({ city, region } = {}) {
  const place = city || region;

  return place ? `Gala dinner for 200 guests in ${place}` : fallbackDescription;
}

/** A real example number for the country, shown international and national, e.g. Australia */
export function phonePlaceholder({ country } = {}) {
  const region = (country && /^[A-Za-z]{2}$/.test(country) ? country.toUpperCase() : '')
    || defaultPhoneCountry();

  if (!region) return fallbackPhone;

  try {
    const example = getExampleNumber(region, examples);

    if (!example) return fallbackPhone;

    return `${example.formatInternational()} or ${example.formatNational()}`;
  } catch (error) {
    return fallbackPhone;
  }
}
